"use client"

import Back from "@modules/common/icons/back"
import FastDelivery from "@modules/common/icons/fast-delivery"
import Refresh from "@modules/common/icons/refresh"

import Accordion from "./accordion"
import { HttpTypes } from "@medusajs/types"

type ProductTabsProps = {
  product: HttpTypes.StoreProduct
}

// product.metadata.attributes is a named spec map, e.g.
// { "Core Row Quantity": "3 Row", "Core Width": "20.75" }.
const getSpecs = (product: HttpTypes.StoreProduct): [string, string][] => {
  const attributes = product.metadata?.attributes

  if (!attributes || typeof attributes !== "object" || Array.isArray(attributes)) {
    return []
  }

  const seen = new Set<string>()
  const specs: [string, string][] = []

  for (const [key, raw] of Object.entries(attributes as Record<string, unknown>)) {
    const label = key.trim()
    const value =
      typeof raw === "string" || typeof raw === "number" ? `${raw}`.trim() : ""

    if (!label || !value || value === "-") {
      continue
    }

    const dedupeKey = `${label.toLowerCase()}|${value.toLowerCase()}`
    if (seen.has(dedupeKey)) {
      continue
    }

    seen.add(dedupeKey)
    specs.push([label, value])
  }

  return specs
}

const ProductTabs = ({ product }: ProductTabsProps) => {
  const specs = getSpecs(product)

  const tabs = [
    {
      label: "Product Information",
      component: <ProductInfoTab product={product} />,
    },
    {
      label: "Shipping & Returns",
      component: <ShippingInfoTab />,
    },
  ]

  return (
    <div className="w-full flex flex-col gap-y-6">
      {specs.length > 0 && (
        <div className="flex flex-col gap-y-3" data-testid="product-specs">
          <h3 className="text-base font-semibold text-ui-fg-base">
            Specifications
          </h3>
          <table className="w-full border border-ui-border-base rounded-base overflow-hidden text-sm">
            <tbody>
              {specs.map(([label, value], index) => (
                <tr
                  key={label}
                  className={index % 2 === 0 ? "bg-grey-5" : "bg-white"}
                >
                  <th
                    scope="row"
                    className="px-3 py-2 text-left align-top font-medium text-ui-fg-subtle w-1/2"
                  >
                    {label}
                  </th>
                  <td className="px-3 py-2 text-ui-fg-base">{value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Accordion type="multiple">
        {tabs.map((tab, i) => (
          <Accordion.Item
            key={i}
            title={tab.label}
            headingSize="medium"
            value={tab.label}
          >
            {tab.component}
          </Accordion.Item>
        ))}
      </Accordion>
    </div>
  )
}

const ProductInfoTab = ({ product }: ProductTabsProps) => {
  return (
    <div className="text-small-regular py-8">
      <div className="grid grid-cols-2 gap-x-8">
        <div className="flex flex-col gap-y-4">
          <div>
            <span className="font-semibold">Material</span>
            <p>{product.material ? product.material : "-"}</p>
          </div>
          <div>
            <span className="font-semibold">Country of origin</span>
            <p>{product.origin_country ? product.origin_country : "-"}</p>
          </div>
          <div>
            <span className="font-semibold">Type</span>
            <p>{product.type ? product.type.value : "-"}</p>
          </div>
        </div>
        <div className="flex flex-col gap-y-4">
          <div>
            <span className="font-semibold">Weight</span>
            <p>{product.weight ? `${product.weight} g` : "-"}</p>
          </div>
          <div>
            <span className="font-semibold">Dimensions</span>
            <p>
              {product.length && product.width && product.height
                ? `${product.length}L x ${product.width}W x ${product.height}H`
                : "-"}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

const ShippingInfoTab = () => {
  return (
    <div className="text-small-regular py-8">
      <div className="grid grid-cols-1 gap-y-8">
        <div className="flex items-start gap-x-2">
          <FastDelivery />
          <div>
            <span className="font-semibold">Fast delivery</span>
            <p className="max-w-sm">
              Your package will arrive in 3-5 business days at your pick up
              location or in the comfort of your home.
            </p>
          </div>
        </div>
        <div className="flex items-start gap-x-2">
          <Refresh />
          <div>
            <span className="font-semibold">Simple exchanges</span>
            <p className="max-w-sm">
              Is the fit not quite right? No worries - we&apos;ll exchange your
              product for a new one.
            </p>
          </div>
        </div>
        <div className="flex items-start gap-x-2">
          <Back />
          <div>
            <span className="font-semibold">Easy returns</span>
            <p className="max-w-sm">
              Just return your product and we&apos;ll refund your money. No
              questions asked – we&apos;ll do our best to make sure your return
              is hassle-free.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProductTabs
